/// <reference types="@types/dom-navigation" />
import { LitElement, html, css, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import Keycloak from 'keycloak-js';
import styles from './styles.js';

/**
 *
 */
@customElement('bundid-element')
export class BundidElement extends LitElement {
    static styles = [
        unsafeCSS(styles),
        css``,
    ];

    keycloak : Keycloak | undefined;

    loginUrl : string | undefined;

    logoutUrl : string | undefined;

    @state()
    visualPulseBundId : boolean = true;

    @state()
    loginWindowProxy : Window | null = null;

    @state()
    loginWindowBlocked : boolean = false;

    /**
     *
     */
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('loginclosed', () => { console.log('Login window closed.'); });
        this.initKeycloak().then((keycloakInitResult) => {
            if (keycloakInitResult) {
                this.keycloak = keycloakInitResult.keycloak;
                this.loginUrl = keycloakInitResult.loginUrl;
                this.logoutUrl = keycloakInitResult.logoutUrl;
                console.log('Keycloak initialized');
            }
        });
    }

    /**
     *
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('logindata', (event : CustomEvent) => this.receiveLoginData(event));
        this.bundIdLogout();
    }

    /**
     *
     * @param event
     */
    async receiveLoginData(event : CustomEvent) {
        window.location.hash = event.detail;
        console.log('Received login data: ', new URLSearchParams(decodeURIComponent(event.detail.slice(1))));
        console.log('Re-initializing Keycloak adapter...');
        const keycloakInitResult = await this.initKeycloak();
        if (keycloakInitResult) {
            this.keycloak = keycloakInitResult.keycloak;
            this.loginUrl = keycloakInitResult.loginUrl;
            this.logoutUrl = keycloakInitResult.logoutUrl;
        } else console.warn('Could not re-initialize Keycloak adapter during receiveLoginData().');
    }

    /**
     *
     */
    async initKeycloak() : Promise<{ keycloak : Keycloak; loginUrl : string; logoutUrl : string } | undefined> {
        const keycloak = new Keycloak({
            url: 'https://bundid.spreewunder.de',
            realm: 'DEMO',
            clientId: 'bundid-antrag',
        });
        let loginUrl : string | undefined;
        let logoutUrl : string | undefined;
        if (keycloak) {
            keycloak.onAuthSuccess = () => {
                console.log('Keycloak: successfully authenticated');
                console.log('token: ', keycloak.token);
                console.log('tokenParsed: ', keycloak.tokenParsed);
                console.log('subject: ', keycloak.subject);
                console.log('idToken: ', keycloak.idToken);
                console.log('idTokenParsed: ', keycloak.idTokenParsed);
                console.log('realmAccess: ', keycloak.realmAccess);
                console.log('resourceAccess: ', keycloak.resourceAccess);
                console.log('refreshToken: ', keycloak.refreshToken);
                console.log('refreshTokenParsed: ', keycloak.refreshTokenParsed);
                console.log('timeSkew: ', keycloak.timeSkew);
            };
            keycloak.onAuthError = () => console.log('Keycloak: error during authentication');
            keycloak.onAuthRefreshSuccess = () => console.log('Keycloak: token is refreshed');
            keycloak.onAuthRefreshError = () => console.log('Keycloak: there was an error while trying to refresh the token');
            keycloak.onAuthLogout = () => console.log('Keycloak: the user is logged out');
            keycloak.onTokenExpired = () => console.log('Keycloak: token just expired');
            keycloak.onReady = () => {
                console.log('Keycloak: ready');
                if (keycloak.idTokenParsed) this.dispatchEvent(new Event(
                    'idtokenupdate',
                    {
                        bubbles: true,
                        composed: true,
                    },
                ));
            };
            keycloak.onActionUpdate = () => console.log('Keycloak: action update');
            try {
                // console.log('Initializing Keycloak stub ...');
                await keycloak.init({
                    onLoad: 'check-sso', // 'check-sso', 'login-required'
                    silentCheckSsoRedirectUri: `${location.origin}/silentCheckSsoLanding.html`,
                    flow: 'hybrid', // 'standard', 'implicit' or 'hybrid'
                    // scope: 'name address',
                    enableLogging: true,
                });
                loginUrl = await keycloak.createLoginUrl({ redirectUri: 'https://bundid.spreewunder.de/loginLanding.html' });
                logoutUrl = keycloak.createLogoutUrl({
                    logoutMethod: 'POST',
                    redirectUri: 'https://bundid.spreewunder.de/logoutLanding.html',
                });
                return { keycloak, loginUrl, logoutUrl };
            } catch(error) { console.error('Failed to initialize adapter:', error); }
        }
        return undefined;
    }

    /**
     *
     */
    async bundIdLogin() : Promise<void> {
        if (this.keycloak && (!this.keycloak.authenticated)) {
            console.log('User is not yet authenticated. Starting login ...');
            try {
                // await this.keycloak.login({ redirectUri: 'https://bundid.spreewunder.de/loginLanding.html' });
                // console.log('Login successful ... but how did we reach this code point?');
                this.loginWindowProxy = open(this.loginUrl, 'login');
                if (!this.loginWindowProxy) this.loginWindowBlocked = true;
            } catch(error) { console.warn('Login unsuccessful: ', error); }
        } else { console.log('User is already authenticated'); }
    }

    /**
     *
     */
    bundIdLogout() {
        if (this.keycloak) {
            try {
                open(this.logoutUrl, 'logout');
                console.log('Logged out.');
            } catch(error) { console.error('Logout failed:', error); }
        }
    }

    /**
     *
     */
    firstUpdated() {
        window.addEventListener('logindata', (event : CustomEvent) => this.receiveLoginData(event));
        setTimeout(() => this.visualPulseBundId = false, 10000);
    }

    /**
     *
     */
    render() {
        return html`
            <div class="flex">
                <div class="flex flex-col">
                    <button @click=${this.bundIdLogin}
                            class="btn bg-bundid-blue flex flex-col p-4 size-fit drop-shadow-lg mr-4">
                        <img src="/images/BundID_logo_white.svg" alt="BundID logo">
                        <div class="prose prose-sm font-normal text-white ${this.visualPulseBundId ? 'animate-pulse' : ''}">
                            Mit BundID anmelden
                        </div>
                    </button>
                    <img class="h-20 object-contain mt-5" src="/images/unicorn.webp" alt="Unicorn image">
                </div>
                <div class="prose prose-sm bg-base-100 rounded-box px-4 max-w-1/2">
                    <p>
                    <h4>Ihr BundID-Vorteil:</h4>
                    <ul class="list-disc">
                        <li>Automatische Übernahme von Daten in Ihren Antrag</li>
                        <li>Wenn Ihre Daten aus einem Ausweisdokument stammen, gelten sie als verifiziert</li>
                        <li>Sie erhalten <b>schnelle Rückmeldung</b> zum Antrag in Ihr <b>BundID-Postfach</b></li>
                    </ul>
                    </p>
                    <div ?hidden=${!this.loginWindowBlocked} class="text-error"><hr> The attempt to open a login window was blocked by your browser</div>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'bundid-element' : BundidElement;
    }
}
